<template>
  <ElButton
    @click="handleClick"
    :disabled="disabled"
    :autofocus="autofocus"
    :type="nativeType"
    :class="[
      dropdown || newDropdown && 'el-button--dropdown',
      kind && `el-button--${kind}`,
      opacity && 'el-button--unimpressed',
      size && `el-button--${size || ''}`,
      type && `el-button--${type || ''}`,
      fullWidth && !icon && 'el-button--full-width',
      fullWidth && icon && 'el-button--full-width-with-icon',
      {
        'is-circle': circle,
        'is-disabled': disabled,
        'is-loading': loading,
        'is-no-border': noBorder,
        'is-like-anchor': likeAnchor,
        'is-plain': plain,
        'is-round': round,
        'el-button--long-text': withLongText,
        'el-button-warning': warningNew,
        'el-button--new-design': newDesign,
        'el-button--navbar': isNavbarButton,
        'el-button--state-badge': hasStateBadge,

      },
    ]"
  >
    <i
      class="el-icon-loading"
      v-if="loading"
    />
    <i
      :class="icon"
      v-if="icon && !loading"
    />
    <span v-if="$slots.default">
      <slot />
    </span>
    <i
      class="icon icon--right-side icon-chevron-down-small"
      v-if="newDropdown && !loading"
    />
  </ElButton>
</template>

<script>
import { Button as ElButton } from 'element-ui';

export default {
  components: {
    ElButton,
  },
  props: {
    autofocus: Boolean,
    circle: Boolean,
    disabled: Boolean,
    dropdown: Boolean,
    fullWidth: Boolean,
    hasStateBadge: Boolean,
    icon: String,
    isNavbarButton: Boolean,
    kind: String,
    likeAnchor: Boolean,
    link: String,
    loading: Boolean,
    nativeType: String,
    newDesign: Boolean,
    newDropdown: Boolean,
    noBorder: Boolean,
    opacity: Boolean,
    plain: Boolean,
    query: Object,
    round: Boolean,
    size: String,
    to: String,
    type: String,
    warningNew: Boolean,
    withLongText: Boolean,
  },
  methods: {
    handleClick(evt) {
      if (this.$attrs['data-analytics-id']) {
        this.$store.dispatch('analytics/trackEvent', {
          analytics: {
            category: this.$route.name,
            action: 'click',
            label: this.$attrs['data-analytics-id'],
          },
        });
      }

      this.$emit('click', evt);

      if (this.to) {
        this.$router.push({
          name: this.to,
          query: this.query,
        });
      }

      if (this.link) {
        const link = this.$router.resolve({
          name: this.link,
        }).href;

        window.location = link;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.el-button {
  position: relative;
  font-weight: 400;
  text-decoration: none;
  transition: background-color .2s ease-in-out;
  &:hover {
    text-decoration: none;
  }

  .icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: inherit;
    display: flex;
    & + span {
      padding-left: rem(25px);
    }
  }

  .icon--right-side {
    right: 0;
    transition: transform .15s ease;

    .el-dropdown.open > & {
      transform: translateY(-50%) rotateZ(-180deg);
    }
  }

  &.is-like-anchor {
    border: 0;
    background: transparent;
    padding: rem(4px) rem(6px);
    text-decoration: underline;
  }

  &.is-loading {
    &::before {
      inset: -2px;
    }
  }
}

.el-button--primary {
  &:hover {
    background-color: $--color-primary-dark-1;
    border-color: $--color-primary-dark-1;
  }

  &:active {
    background-color: darken($--color-primary, 5%);
    border-color: darken($--color-primary, 5%);
  }

  &:focus {
    background-color: $--color-primary;
    border-color: $--color-primary;
    box-shadow: 0 0 0 rem(3px) rgba($--color-primary, .4);
  }
}

.el-button--info {
  &:hover {
    background-color: darken($--color-info, 5%);
    border-color: darken($--color-info, 5%);
  }

  &:active {
    background-color: darken($--color-info, 2%);
    border-color: darken($--color-info, 2%);
  }

  &:focus {
    background-color: $--color-info;
    border-color: $--color-info;
    box-shadow: 0 0 0 rem(3px) rgba($--color-info, .4);
  }
}

.el-button--danger {
  &:hover {
    background-color: darken($--color-danger, 5%);
    border-color: darken($--color-danger, 5%);
  }

  &:active {
    background-color: darken($--color-danger, 2%);
    border-color: darken($--color-danger, 2%);
  }

  &:focus {
    background-color: $--color-danger;
    border-color: $--color-danger;
    box-shadow: 0 0 0 rem(3px) rgba($--color-danger, .4);
  }
}

.el-button--warning {
  color: $--color-white;

  &.is-plain {
    color: $--color-warning;
    background-color: transparent;
  }
}

.el-button--cta {
  background-color: $--color-success;
  border-color: $--color-success;
  color: $--color-white;

  &:hover {
    background-color: darken($--color-success, 5%);
    border-color: darken($--color-success, 5%);
  }

  &:active {
    background-color: darken($--color-success, 2%);
    border-color: darken($--color-success, 2%);
  }

  &:focus {
    background-color: $--color-success;
    border-color: $--color-success;
    box-shadow: 0 0 0 rem(3px) rgba($--color-success, .4);
  }

  &.is-plain {
    background: $--color-white;
    color: $--color-success;

    &:hover {
      background-color: lighten($--color-success, 52%);
    }

    &:active {
      background-color: lighten($--color-success, 47%);
    }

    &:focus {
      background-color: $--color-white;
      box-shadow: 0 0 0 rem(3px) rgba($--color-success, .4);
    }
  }
}

.el-button--icon {
  padding: 0;
  border: 0;
  height: $--input-height;
  color: $--color-primary;
  background-color: transparent;
  transition: transform .2s ease;
  &:hover {
    transform: scale(1.2);
    background-color: transparent;
  }
  .icon {
    position: static;
    font-size: rem(18px);
    transform: none;
  }
}

.el-button--small {
  .icon {
    font-size: rem(13px);
  }
}

.el-button--tiny {
  padding: rem(4px) rem(6px);
  font-size: rem(12px);
  &.is-round {
    padding: rem(4px) rem(6px);
    font-size: rem(12px);
  }
}

.el-button--medium {
  font-size: rem(14px);
}

.el-button--large {
  padding: $--button-large-padding-vertical $--button-large-padding-horizontal;
  font-size: $--button-large-font-size;
}

.el-button--extra-large {
  padding: $--button-extra-large-padding-vertical $--button-extra-large-padding-horizontal;
  font-size: $--button-extra-large-font-size;
  line-height: 1.3;
  font-weight: 600;

  .icon + span {
    padding-left: rem(30px);
  }
}

.el-button--dropdown {
  padding-right: rem(28px);
  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 3px);
    transform: translateY(calc(50% - 3px));
    right: rem(10px);
    border: solid 5px transparent;
    border-top-color: $--color-white;
  }
}

.el-button--alert {
  background-color: $--color-primary;
  border: 0;
  font-size: rem(12px);
  font-weight: 600;
  padding: rem(5px) rem(10px);
  color: $--color-white;
  &:hover {
    background-color: $--color-primary-dark-2;
    color: $--color-white;
  }
}

.el-button--unimpressed {
  opacity: .6;
}

//new design
@media only screen and (max-width: ($--breakpoint-sm - 1px)) {
  .button-mobile {
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 1;
    font-weight: 600;

    :deep() {
      .icon {
        font-size: 1.5rem;
      }
      .icon + span {
        padding-left: 2rem;
      }
    }
  }

  .el-button--button.button-mobile {
    :deep() {
      .icon {
        text-shadow: 0 0 0 $--color-primary;
      }
    }
    &:hover {
      background-color: $--color-white;
    }
  }
}

.el-button--secondary {
  line-height: 0;
  font-weight: 600;
  background-color: transparent;
  color: $--color-primary;
  border: 1px solid #cbe0ed !important;
  padding: 1.2rem 1rem;

  span {
    vertical-align: middle;
  }

  &:hover {
    background-color: #e6f2f8;
  }

  &:active {
    background-color: #e6f2f8;
  }

  :deep() {
    .icon {
      font-size: 1.5rem;
      text-shadow: 0 0 0 $--color-primary;
      top: 51%;
    }
    .icon + span {
      padding-left: 2rem;
    }
  }
}

.el-button--no-border {
  border: none;
  font-weight: 600;

  :deep() {
    .icon {
      font-size: 1.5rem;
      text-shadow: 0 0 0 $--color-primary;
    }
    .icon + span {
      padding-left: 2rem;
    }
  }

  &:hover {
    background-color: transparent;
    color: darken($--color-primary, 10%);
  }
  &:focus {
    background-color: transparent;
    color: darken($--color-primary, 10%);
  }
}

.el-button--navbar {
  font-size: rem(16px);
  font-weight: 600;
  line-height: 1.4;
  padding: 8px 11px;

  :deep() {
    .icon {
      padding-right: 0;
      font-size: rem(24px);
    }

    .icon + span {
      padding-left: 2.1rem;
    }
  }
}

// ----------------- THE LASTEST DESIGN -----------------
@mixin button-color($bg-color, $border-color: $bg-color, $color: $--color-white) {
  background-color: $bg-color;
  border-color: $border-color;
  color: $color;
}

.el-button--new-design {
  font-size: rem(14px);
  line-height: rem(14px);
  font-weight: 600;
  padding: rem(8px) 12px;
  transition: .1s;

  :deep() {
    .icon,
    .el-icon-loading {
      padding-right: 0;
      font-size: rem(20px);
    }

    .icon::before {
      line-height: rem(24px);
    }

    .icon + span,
    .el-icon-loading + span {
      padding-left: rem(28px);
      margin-left: 0;
    }

    .el-icon-loading {
      line-height: inherit;
      position: absolute;
      width: 20px;
    }
  }

  // ---- Buttons size -----
  &.el-button--small {
    font-size: rem(13px);
    line-height: rem(13px);
    padding: rem(6.5px) 8px;

    :deep() {
      .icon + span,
      .el-icon-loading + span {
        padding-left: rem(24px);
      }
    }
  }

  &.el-button--large {
    font-size: rem(16px);
    line-height: rem(16px);
    padding: rem(11px) 12px;
    min-width: unset;

    :deep() {
      .icon,
      .el-icon-loading {
        font-size: rem(24px);
      }

      .icon::before {
        line-height: rem(24px);
      }

      .icon + span,
      .el-icon-loading + span {
        padding-left: rem(32px);
      }

      .el-icon-loading {
        width: 24px;
      }
    }
  }

  &.el-button--extra-large {
    font-size: rem(20px);
    line-height: rem(20px);
    padding: rem(11px) 16px;

    :deep() {
      .icon,
      .el-icon-loading {
        font-size: rem(26px);
      }

      .icon::before {
        line-height: rem(26px);
      }

      .icon + span,
      .el-icon-loading + span {
        padding-left: rem(34px);
      }

      .el-icon-loading {
        width: 26px;
      }
    }
  }

  &.el-button--mega {
    font-size: rem(20px);
    line-height: rem(20px);
    padding: rem(17px) 24px;

    :deep() {
      .icon,
      .el-icon-loading {
        font-size: rem(30px);
      }

      .icon::before {
        line-height: rem(30px);
      }

      .icon + span,
      .el-icon-loading + span {
        padding-left: rem(38px);
      }

      .el-icon-loading {
        width: 30px;
      }
    }
  }

  // ---- Buttons variant -----
  &.is-no-border {
    border-color: transparent !important;

    &:hover,
    &:focus,
    &:active,
    &.active {
      border-color: transparent !important;
    }
  }

  // ---- Buttons type (colors) -----
  &.el-button--primary {
    @include button-color($--color-primary);

    &:hover,
    &:focus,
    &:active,
    &.active {
      @include button-color($--color-primary-dark-3);

      box-shadow: unset;
    }

    &.is-plain {
      @include button-color(transparent, $--border-color-base, $--color-primary);

      &:hover,
      &:focus,
      &:active,
      &.active {
        color: $--color-primary-dark-3;
      }
    }
  }

  &.el-button--warning {
    @include button-color($--color-warning);

    &:hover,
    &:focus,
    &:active,
    &.active {
      @include button-color($--color-warning-2);
    }

    &.is-plain {
      @include button-color(transparent, $--color-warning, $--color-warning);

      &:hover,
      &:focus,
      &:active,
      &.active {
        color: $--color-warning-2;
      }
    }
  }

  &.el-button--danger {
    @include button-color($--color-danger-2);

    &:hover,
    &:focus,
    &:active,
    &.active {
      @include button-color($--color-danger-3);
    }

    &.is-plain {
      @include button-color(transparent, $--color-danger-2, $--color-danger-2);

      &:hover,
      &:focus,
      &:active,
      &.active {
        color: $--color-danger-3;
        box-shadow: none;
      }
    }
  }

  &.el-button--success {
    @include button-color($--color-success-2);

    &:hover,
    &:focus,
    &:active,
    &.active {
      @include button-color($--color-success-3);
    }

    &.is-plain {
      @include button-color(transparent, $--color-success-2, $--color-success-2);

      &:hover,
      &:focus,
      &:active,
      &.active {
        color: $--color-success-3;
        box-shadow: none;
      }
    }
  }

  // ---- Only icon button -----
  @mixin only-icon-button {
    height: unset;
    padding-left: 5px;
    padding-right: 5px;

    &:hover,
    &:focus,
    &:active,
    &.active {
      transform: none;
    }

    .icon,
    .el-icon-loading {
      position: static;
      transform: none;
    }

    .el-icon-loading {
      display: flex;
    }

    &.el-button--small {
      padding-left: 3px;
      padding-right: 3px;
    }

    &.el-button--large {
      padding-left: 7px;
      padding-right: 7px;
    }

    &.el-button--extra-large {
      padding-left: 8px;
      padding-right: 8px;
    }

    &.el-button--mega {
      padding-left: 10.5px;
      padding-right: 10.5px;
    }
  }

  @mixin responsive-only-icon($size) {
    @media only screen and (max-width: $size) {
      @include only-icon-button;

      :deep() {
        .icon + span,
        .el-icon-loading + span {
          display: none;
        }
      }
    }
  }

  // TODO: rename to '...--icon' after redesign
  &.el-button--icon-new {
    @include only-icon-button;
  }

  &.el-button--only-icon-for-xxs {
    @include responsive-only-icon($--xxs - 1px);
  }

  &.el-button--only-icon-for-xs {
    @include responsive-only-icon($--xs - 1px);
  }

  &.el-button--only-icon-for-sm {
    @include responsive-only-icon($--sm - 1px);
  }

  &.el-button--only-icon-for-lg {
    @include responsive-only-icon($--lg - 1px);
  }

  // ---- Button utilites ----
  &.el-button--dropdown {
    padding-right: rem(20px);

    &::after {
      content: unset;
    }
  }

  &.el-button--full-width {
    width: 100%;

    :deep() {
      .icon + span {
        padding-left: 0;
      }
    }
  }

  &.el-button--full-width-with-icon {
    width: 100%;

    :deep() {
      > span {
        position: relative;
      }

      .icon {
        left: 0;
        top: 51%;
      }
    }
  }

  &.el-button--long-text {
    white-space: normal;

    :deep() {
      > span,
      .icon,
      .icon + span {
        display: inline-block;
      }
    }
  }

  &.el-button--state-badge {
    &::before {
      content: '';
      background: $--color-danger-2;
      border-radius: 50%;
      border: 2px solid $--color-white;
      height: 12px;
      width: 12px;
      position: absolute;
      right: -5px;
      top: -5px;
    }
  }

  &.el-button--dashed {
    border-style: dashed;
  }

  // ---- Disable state - at the end due to specificity -----
  &:disabled,
  &.disabled,
  &.is-disabled {
    @include button-color($--color-gray-9, $--color-gray-9, $--color-white);

    pointer-events: none;
  }

  &.is-plain {
    &:disabled,
    &.disabled,
    &.is-disabled {
      background-color: transparent;
      color: $--color-gray-9;
    }
  }
}
</style>
